import { isString } from '../shared/runtime-values.js';
import { z } from 'zod';
import type { DataLayer } from '../shared/data-layer.js';
import { calculateDataLayerBounds } from '../shared/data-layer.js';
import { importGeoJsonDataLayer } from './data-layer-import.js';
import type { RoutePlannerModel } from './route-planner-state.js';
import { isResolvedRoutePlannerWaypoint } from './route-planner-waypoints.js';
import { t } from './i18n.js';

const stopSchema = z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180), address: z.string() });
const metadataSchema = z.object({ travelMode: z.enum(['driving', 'walking', 'cycling']), routeStops: z.array(stopSchema).min(2).max(100) });
const oldMetadataSchema = z.object({ travelMode: z.enum(['driving', 'walking', 'cycling']), stops: z.array(z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)])).min(2).max(100) });

export function readMapRoute(layer: DataLayer) {
  if (layer.features.length !== 1 || layer.features[0].geometry?.type !== 'LineString') return null;
  const properties = layer.features[0].properties;
  const parsed = metadataSchema.safeParse(properties);
  if (parsed.success) return parsed.data;
  const old = oldMetadataSchema.safeParse(properties);
  return old.success ? { travelMode: old.data.travelMode, routeStops: old.data.stops.map(([lng, lat]) => ({ lat, lng, address: t('map_route.map_point') })) } : null;
}

export function mapRouteLayer(model: RoutePlannerModel, previous: DataLayer | null, color = previous?.features[0].sourceStyle?.color ?? previous?.style.color ?? '#2563eb'): DataLayer | null {
  const route = model.selectedRoute();
  if (!route) return null;
  const stops = model.waypoints.filter(isResolvedRoutePlannerWaypoint);
  const customName = previous?.features[0].properties.routeTitle;
  const name = isString(customName) && customName.trim() ? customName : `${stops[0].address} → ${stops.at(-1)!.address}`.slice(0, 120);
  const properties = { name, travelMode: model.mode, distanceKm: route.distanceKm, durationSec: route.durationSec,
    routeStops: stops.map(({ lat, lng, address }) => ({ lat, lng, address })), stops: stops.map(({ lat, lng }) => [lng, lat]) };
  const geometry = { type: 'LineString', coordinates: route.coords };
  const { layer } = importGeoJsonDataLayer({ type: 'FeatureCollection', features: [{ type: 'Feature', properties, geometry }] }, { name, color });
  if (!layer || !previous) return layer;
  const feature = { ...previous.features[0], sourceStyle: { ...previous.features[0].sourceStyle, color }, geometry: layer.features[0].geometry, properties: { ...previous.features[0].properties, ...properties } };
  return { ...previous, style: { ...previous.style, color }, name, features: [feature], bounds: calculateDataLayerBounds([feature]), saved: false };
}
